import React,{useState,useEffect} from 'react';
import {useParams,Link,useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useSupabase} from '../contexts/SupabaseContext';
import {useAuth} from '../contexts/AuthContext';
import EmbedCodeGenerator from '../components/EmbedCodeGenerator';
import toast from 'react-hot-toast';
import {format} from 'date-fns';

const {FiArrowLeft,FiEdit3,FiActivity,FiEye}=FiIcons;

const CampaignDetails=()=> {
  const {id}=useParams();
  const navigate=useNavigate();
  const [campaign,setCampaign]=useState(null);
  const [loading,setLoading]=useState(true);
  const supabase=useSupabase();
  const {user}=useAuth();

  useEffect(()=> {
    fetchCampaign();
  },[id]);

  const fetchCampaign=async ()=> {
    try {
      const {data,error}=await supabase
        .from('campaigns')
        .select('*')
        .eq('id',id)
        .eq('owner_id',user.id)
        .single();

      if (error) throw error;
      setCampaign(data);
    } catch (error) {
      toast.error('Failed to fetch campaign details');
      console.error('Error:',error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive=async ()=> {
    try {
      const {error}=await supabase
        .from('campaigns')
        .update({is_active: !campaign.is_active})
        .eq('id',id)
        .eq('owner_id',user.id);

      if (error) throw error;
      setCampaign({...campaign,is_active: !campaign.is_active});
      toast.success(`Campaign ${!campaign.is_active ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error('Failed to update campaign status');
      console.error('Error:',error);
    }
  };

  const copyEmbedCode=()=> {
    const embedUrl = `${window.location.origin}/v1/c.js`;
    const embedCode = `<script src="${embedUrl}" data-cid="${campaign.public_id}" async></script>`;
    navigator.clipboard.writeText(embedCode);
    toast.success('Embed code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Campaign not found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-500">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <button onClick={()=> navigate('/dashboard')} className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors">
                <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
                <p className="text-gray-600 mt-1">
                  Created on {format(new Date(campaign.created_at),'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`px-3 py-1 text-sm font-medium rounded-full ${
                campaign.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {campaign.is_active ? 'Active' : 'Inactive'}
              </div>
              <button
                onClick={toggleActive}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  campaign.is_active 
                    ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                }`}
              >
                {campaign.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <Link
                to={`/campaigns/${campaign.id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiEdit3} className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Campaign Configuration */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiActivity} className="h-5 w-5 mr-2" />
                Configuration
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Product Name</label>
                  <p className="mt-1 text-sm text-gray-900">{campaign.product_name}</p>
                </div>
                {campaign.price && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price</label>
                    <p className="mt-1 text-sm text-gray-900">${campaign.price}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message Template</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {campaign.message_template}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Min Interval</label>
                    <p className="mt-1 text-sm text-gray-900">{campaign.interval_min_ms / 1000}s</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Interval</label>
                    <p className="mt-1 text-sm text-gray-900">{campaign.interval_max_ms / 1000}s</p>
                  </div>
                </div>
                {campaign.names && campaign.names.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Custom Names</label>
                    <p className="mt-1 text-sm text-gray-900">{campaign.names.join(', ')}</p>
                  </div>
                )}
                {campaign.cities && campaign.cities.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Custom Cities</label>
                    <p className="mt-1 text-sm text-gray-900">{campaign.cities.join(', ')}</p>
                  </div>
                )}
                {campaign.allowed_domains && campaign.allowed_domains.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Allowed Domains</label>
                    <p className="mt-1 text-sm text-gray-900">{campaign.allowed_domains.join(', ')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Integration */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiEye} className="h-5 w-5 mr-2" />
                Integration
              </h2>
              <EmbedCodeGenerator campaign={campaign} onCopy={copyEmbedCode} />
            </div>
          </div>

          {/* Preview Section */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiEye} className="h-5 w-5 mr-2" />
              Preview
            </h2>
            <div className="bg-gray-100 p-8 rounded-lg relative overflow-hidden min-h-[200px]">
              <div className="text-center text-gray-600">
                <p>Your website content would appear here</p>
                <p className="text-sm mt-2">Social proof notifications will overlay on top</p>
              </div>
              {/* Sample Toast */}
              <motion.div
                initial={{opacity: 0,y: 20,scale: 0.9}}
                animate={{opacity: 1,y: 0,scale: 1}}
                transition={{duration: 0.5,delay: 0.5}}
                className="absolute bottom-4 left-4 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-sm">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {campaign.message_template
                        .replace('{{name}}','Sarah')
                        .replace('{{city}}','Jakarta')
                        .replace('{{product_name}}',campaign.product_name)}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CampaignDetails;